import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, FormProps, Select } from 'antd'
import { isPercentageFeeType } from '@/components/Freight/shared'
import { AccessorialSelector } from './AccessorialSelector'
import type { TransactionDetail } from '../../columnDefs'
import type { InvoiceDetail } from '@/api/useFreight/types'
import type { AccessorialFormType as AccessorialFormType, AccessorialOptionType } from '../types'
import { FooterButtons } from './FooterButtons'
import { useMemo } from 'react'
import { useReasonCodes } from '@/api/useMetadata/useMasterMetadata'
import { TotalInput } from './TotalInput'
import { AmountInput } from './AmountInput'
import { DefaultOptionType } from 'antd/es/select'
import { IconInfoCircle } from '@tabler/icons-react'

type AccessorialFormProps = {
  accessorialOptions?: AccessorialOptionType[]
  terminalOptions: DefaultOptionType[]
  storeOptions: DefaultOptionType[]
  isTerminalsLoading?: boolean
  isStoresLoading?: boolean
  isAccessorialsLoading?: boolean
  isAccessorialMutationPending: boolean
  currentAccessorial?: TransactionDetail | InvoiceDetail | undefined
  showRateAndTotal?: boolean
  groupLocations?: boolean
} & FormProps

export function AccessorialForm({
  accessorialOptions,
  terminalOptions,
  storeOptions,
  isTerminalsLoading,
  isStoresLoading,
  isAccessorialsLoading,
  isAccessorialMutationPending,
  currentAccessorial,
  showRateAndTotal = true,
  groupLocations = true,
  ...formProps
}: AccessorialFormProps) {
  const accessorialId = Form.useWatch<string>('accessorial_id', formProps.form)
  const { data: reasonCodeResponse = { codes: [] }, isLoading: isReasonCodesLoading } = useReasonCodes()
  const reasonCodeOptions = useMemo(
    () =>
      reasonCodeResponse.codes.map((rc) => {
        return { label: rc.reason.replace(/\s+/g, '_'), value: rc.reason }
      }),
    [reasonCodeResponse]
  )

  const selectedAccessorial = useMemo(() => {
    const selected = currentAccessorial ?? accessorialOptions?.find((opt) => opt.value === accessorialId)
    const feeType =
      (selected as AccessorialOptionType)?.feeType ||
      (selected as AccessorialFormProps['currentAccessorial'])?.uom ||
      ''
    return {
      accessorial: selected,
      feeType: feeType,
      isPercentageBased: isPercentageFeeType(feeType),
      showReasonCode: !!reasonCodeOptions?.length && (selected as AccessorialOptionType)?.requiresReasonCode,
    }
  }, [accessorialId, accessorialOptions, currentAccessorial])
  const isEditing = !!currentAccessorial
  const isSurcharge = currentAccessorial?.type === 'Surcharge'
  const areLocationsLoading = isTerminalsLoading || isStoresLoading

  return (
    <Form layout='vertical' {...formProps}>
      <Vertical gap={8} className='py-2'>
        {!isEditing && accessorialOptions ? (
          <AccessorialSelector
            options={accessorialOptions}
            isLoading={isAccessorialsLoading}
            showRate={showRateAndTotal}
          />
        ) : (
          <Vertical>
            <Texto>Subtype</Texto>
            <Texto weight={'bold'}>{currentAccessorial?.subtype}</Texto>
          </Vertical>
        )}
        {!isSurcharge && (
          <Horizontal gap={8}>
            <Form.Item name='origin' label='Origin' style={{ marginBottom: 0, flex: 1 }}>
              <Select
                placeholder='Select origin'
                options={
                  groupLocations
                    ? [
                        { label: 'Terminals', options: terminalOptions },
                        { label: 'Stores', options: storeOptions },
                      ]
                    : terminalOptions.concat(storeOptions)
                }
                showSearch
                labelInValue
                loading={areLocationsLoading}
                optionFilterProp={'label'}
              />
            </Form.Item>
            <Form.Item name='destination' label='Destination' style={{ marginBottom: 0, flex: 1 }}>
              <Select
                placeholder='Select destination'
                options={
                  groupLocations
                    ? [
                        { label: 'Stores', options: storeOptions },
                        { label: 'Terminals', options: terminalOptions },
                      ]
                    : storeOptions.concat(terminalOptions)
                }
                showSearch
                labelInValue
                loading={areLocationsLoading}
                optionFilterProp={'label'}
              />
            </Form.Item>
          </Horizontal>
        )}
        {selectedAccessorial.isPercentageBased ? (
          showRateAndTotal ? (
            <TotalInput selectedAccessorial={selectedAccessorial} isEditing={isEditing} />
          ) : (
            <Texto appearance='hint' className='mt-2'>
              <IconInfoCircle size={12} /> This accessorial is based on a percentage rate and its amount cannot be
              edited.
            </Texto>
          )
        ) : (
          <AmountInput selectedAccessorial={selectedAccessorial} showRateAndTotal={showRateAndTotal} />
        )}
        {selectedAccessorial.showReasonCode && (
          <Form.Item
            label='Reason Code'
            name='reason_code'
            rules={[{ required: true, message: 'Please select a reason code' }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder='Select a reason code'
              options={reasonCodeOptions}
              allowClear
              loading={isReasonCodesLoading}
            />
          </Form.Item>
        )}
      </Vertical>
      <FooterButtons isLoading={isAccessorialMutationPending} />
    </Form>
  )
}
